import React from "react";
import { Link } from "react-router-dom";
import { Nav } from "@/components/layout/Nav";
import { Footer } from "@/components/layout/Footer";
import { PageSEO } from "@/components/seo/PageSEO";
import { SITE_URL } from "@/utils/seo";
import { Home, Clock, CheckCircle, ArrowRight } from "lucide-react";

const SITE = SITE_URL;

const breadcrumbSchema = {
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: [
    { "@type": "ListItem", position: 1, name: "Home", item: `${SITE}/` },
    { "@type": "ListItem", position: 2, name: "Delivery Areas", item: `${SITE}/delivery` },
    { "@type": "ListItem", position: 3, name: "Vacation Homes", item: `${SITE}/delivery/vacation-homes` },
  ],
};

export default function VacationHomeDelivery() {
  return (
    <>
      <PageSEO
        title="Stroller Delivery to Vacation Homes, Airbnb & VRBO | Orlando"
        description="Enchanted Gear Rentals delivers strollers and baby gear to Airbnb, VRBO and vacation rental homes across the Orlando area. Free standard delivery, flexible windows."
        canonical={`${SITE}/delivery/vacation-homes`}
        structuredData={breadcrumbSchema}
      />
      <Nav />
      <main className="min-h-screen" style={{ background: "#040A0F" }}>
        <section className="pt-28 pb-14 text-center" style={{ background: "linear-gradient(180deg, #060f18 0%, #040A0F 100%)" }}>
          <div className="container max-w-3xl">
            <nav aria-label="Breadcrumb" className="text-xs text-[#B9C8D5] mb-4">
              <Link to="/" className="hover:text-[#FFD166]">Home</Link>
              <span className="mx-2 opacity-40">/</span>
              <Link to="/delivery" className="hover:text-[#FFD166]">Delivery Areas</Link>
              <span className="mx-2 opacity-40">/</span>
              <span className="text-[#FFD166]">Vacation Homes</span>
            </nav>
            <h1 className="font-serif text-4xl md:text-5xl text-[#F6F1E4] mb-5">Stroller Delivery to Vacation Homes &amp; Rentals</h1>
            <p className="text-[#B9C8D5] text-lg max-w-2xl mx-auto">
              Staying at an Airbnb, VRBO or vacation rental near Disney World or Universal? We deliver premium strollers and baby gear directly to your door — free standard delivery included.
            </p>
          </div>
        </section>

        <div className="container max-w-3xl py-12 pb-20 space-y-12">
          <section>
            <h2 className="font-serif text-2xl text-[#F6F1E4] mb-5">How vacation-home delivery works</h2>
            <div className="space-y-4">
              {[
                { step: "1", title: "Reserve online", body: "Book your stroller and enter your rental home address as the delivery location." },
                { step: "2", title: "Choose a delivery window", body: "Pick any 1-hour window from 7 am to 10 pm that fits your schedule." },
                { step: "3", title: "We meet you at the door", body: "We deliver to your vacation home, Airbnb or VRBO. Someone will need to be present to receive the stroller." },
                { step: "4", title: "Pickup when you leave", body: "We collect the stroller at the end of your rental. We will arrange a pickup time that works with your checkout." },
              ].map(({ step, title, body }) => (
                <div key={step} className="flex gap-4">
                  <div className="w-8 h-8 rounded-full bg-[#FFD166]/15 border border-[#FFD166]/30 flex items-center justify-center text-[#FFD166] font-bold text-sm shrink-0">{step}</div>
                  <div>
                    <div className="font-semibold text-[#F6F1E4]">{title}</div>
                    <div className="text-sm text-[#B9C8D5] mt-1">{body}</div>
                  </div>
                </div>
              ))}
            </div>
          </section>

          <section className="grid sm:grid-cols-2 gap-5">
            <div>
              <h2 className="font-serif text-xl text-[#F6F1E4] mb-4">Delivery windows</h2>
              <div className="space-y-2">
                {[
                  { time: "7:00 am – 10:00 pm", note: "Free" },
                  { time: "1-hour windows", note: "Pick the exact hour for you" },
                  { time: "Early morning (7 am)", note: "No extra charge" },
                ].map(({ time, note }) => (
                  <div key={time} className="flex items-center gap-3 rounded-lg bg-[#0A1825] border border-[#FFD166]/10 px-4 py-2.5">
                    <Clock className="w-4 h-4 text-[#FFD166] shrink-0" />
                    <span className="text-[#F6F1E4] text-sm flex-1">{time}</span>
                    <span className="text-xs text-[#B9C8D5]">{note}</span>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <h2 className="font-serif text-xl text-[#F6F1E4] mb-4">Areas served</h2>
              <ul className="space-y-2">
                {["Kissimmee", "Celebration", "Reunion Resort", "Clermont", "Davenport", "Lake Buena Vista", "Windermere", "Winter Garden", "International Drive area"].map((area) => (
                  <li key={area} className="flex items-center gap-2 text-sm text-[#F6F1E4]">
                    <CheckCircle className="w-4 h-4 text-[#FFD166] shrink-0" /> {area}
                  </li>
                ))}
              </ul>
            </div>
          </section>

          <section className="rounded-xl bg-[#0A1825] border border-[#FFD166]/15 p-6">
            <div className="flex gap-3 items-start">
              <Home className="w-5 h-5 text-[#FFD166] shrink-0 mt-0.5" />
              <div>
                <h2 className="font-semibold text-[#F6F1E4] mb-2">Not sure if we serve your address?</h2>
                <p className="text-sm text-[#B9C8D5]">Enter your delivery address when you book and we will confirm availability. You can also call or text us at <a href="tel:+16893457368" className="text-[#FFD166]">689-345-RENT (7368)</a> before booking.</p>
              </div>
            </div>
          </section>

          <div className="text-center">
            <Link to="/#trip-setup"><button className="btn-primary" data-testid="vacation-delivery-cta">Reserve Your Stroller <ArrowRight className="inline w-4 h-4 ml-1" /></button></Link>
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
}
